import { useRef, useEffect } from 'react';
import { Whisper, Tooltip } from 'rsuite';

import BasicButton from '../basicButton/basicButton';

import './basicNumberInput.css';

const MINUS_SVG = 
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M3.80005 12C3.80005 11.4477 4.24776 11 4.80005 11L19.2 11C19.7523 11 20.2001 11.4477 20.2001 12C20.2001 12.5523 19.7523 13 19.2 13L4.80005 13C4.24776 13 3.80005 12.5523 3.80005 12Z" fill="#17C653"/>
    </svg>

const PLUS_SVG =
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.00005 1.80005L9.00005 16.2M16.2 9.00005L1.80005 9.00005" stroke="#17C653" stroke-width="2" stroke-linecap="round"/>
    </svg>

function sanitizeInput(value) {
    return value.replace(/[^0-9.,]/g, '');
  }

  function fixDecimal(value) {
    let result = '';
    let decimalUsed = false;
  
    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      if (ch === '.' || ch === ',') {
        if (!decimalUsed) {
          // First decimal separator becomes '.' 
          decimalUsed = true;
          result += '.';
        }
        // If we already used a decimal separator, skip additional ones
      } else {
        result += ch;
      }
    }
  
    return result;
  }
  
  /**
   * A "fully typed" float must:
   *   - Not be empty
   *   - Contain digits
   *   - Optionally have one decimal point
   *   - Not end in decimal point
   * Examples of "fully typed": "5", "5.0", "123.456"
   * Examples of partial: "5.", "", ".5"
   */
  function isFullyTypedFloat(value) {
    if (!value) return false;
    if (value.endsWith('.')) return false; // trailing decimal -> partial
    // Must match digits, optional decimal, digits
    return /^\d+(\.\d+)?$/.test(value);
  }
  
  /**
   * Main function:
   *   - Remove invalid chars
   *   - Normalize decimal
   *   - If fully typed float, parse & return as number
   *   - Otherwise keep partial string
   */
  function getFloatValue(value) {
    // 1. Clean any invalid chars
    let cleaned = sanitizeInput(value);
  
    // 2. Normalize decimal separators to a single '.'
    cleaned = fixDecimal(cleaned);
  
    // 3. If it's a fully typed float, parse it
    if (isFullyTypedFloat(cleaned)) {
      const num = parseFloat(cleaned);
      // If you want to disallow 0 or negative, clamp to 0 or do something else:
      return num > 0 ? num : 0;
    }
  
    // 4. Partial input => return the (cleaned) string so user can keep typing
    return cleaned;
  }

export default function BasicNumberInput({label, value, spaced, fluid, error, ...props}) {
    const errorRef = useRef(null);

    useEffect(() => {
        if (errorRef.current && error) {
            errorRef.current.open();
        }
        if (errorRef.current && !error) {
            errorRef.current.close();
        }
    }, [errorRef, error]);

    const onChange = (value) => {
        console.log(value);
        props.onChange(getFloatValue(String(value)));
    };

    return (
        <div className='basicNumberInputWrapper paragraph-14R' style={{
            justifyContent: spaced ? "space-between" : "flex-start",
            width: fluid ? "100%" : "max-content",
            color: "var(--Gray-900)"
        }}>
            <Whisper
                placement="bottomStart"
                trigger="none"
                ref={errorRef}
                speaker={<Tooltip style={{
                    color: "var(--danger-normal)",
                }}>{error}</Tooltip>}
            >
            <span style={{width: "max-content"}}>{label}</span>
            </Whisper>
            <div className='basicNumberInputWrapper'>
                <BasicButton className={'minusBtn'} onClick={() => {
                    onChange ( (value || 0) - 1);
                }} {...props}>{MINUS_SVG}</BasicButton>
                <input className='basicNumberInput' type='text' {...props} onChange={(e) => onChange(e.target.value)} placeholder='0' value={value} />
                <BasicButton className={'plusBtn'} onClick={() => {
                    onChange( (value || 0) + 1);
                }} {...props}>{PLUS_SVG}</BasicButton>
            </div>
        </div>
    )
}